import React, {
  useCallback, useEffect, useMemo, useRef, useState, type PointerEvent, type WheelEvent,
} from 'react';
import { Box } from '@mui/material';
import { isAreaFeature, type Coordinate, type EditableFloorPlan, type EditorFeature } from '../models/editor-feature';
import { useCanvasInteraction, type ReshapeMode } from '../hooks/use-canvas-interaction';
import type { EditorDocument } from '../hooks/use-editor-document';
import { useMdiIcons } from '../hooks/use-mdi-icons';
import type { Selection } from '../hooks/use-selection';
import type { Viewport } from '../hooks/use-viewport';
import type { Draft } from '../hooks/use-drawing';
import { buildDrawPlan, type DrawLayerTag } from '../utils/draw-plan';
import { getMdiIconPath } from '../utils/mdi-icons';
import { paint, surfaceTransformOf } from '../utils/paint';
import {
  snapPoint, snapTargetsOf, type SnapResult, type SnapSettings,
} from '../utils/snapping';
import { alignPoints, type AlignmentGuide } from '../utils/alignment';
import { computeBoundingBox, createProjection, toScreen } from '../utils/projection';
import { boundsOf, isResizable } from '../utils/selection-chrome';
import type { LayerVisibility } from '../utils/layer-settings';

// Re-exported from where it now lives, beside the opacity and solo rules that share its
// vocabulary. Kept as an export here because this is where every caller already imports it.
export type { LayerVisibility };

interface Props {
  readonly floorPlan: EditableFloorPlan;
  readonly viewport: Viewport;
  /**
   * The surface's own coordinate space, which the viewBox is expressed in.
   *
   * Not the size of the element: the element fills whatever the stage gives it and the
   * surface is letterboxed into that, exactly as the pointer maths already assumes. Keeping
   * the two apart is what lets the canvas be responsive without every stored annotation size
   * meaning something different on a wider monitor.
   */
  readonly width: number;
  readonly height: number;
  readonly layerVisibility: LayerVisibility;
  readonly selection: Selection;
  readonly document: EditorDocument;
  /** Set while a placement tool is armed; the next click places rather than selects. */
  readonly onPlace?: (at: Coordinate) => void;
  /** Set while a drawing or measuring tool is armed. Returns true when it took the click. */
  /** `straighten`: Shift, Ctrl/⌘ or Alt is held, so the segment being drawn stays level or upright. */
  readonly onDraw?: (at: Coordinate, straighten: boolean) => boolean;
  readonly onDrawMove?: (at: Coordinate, straighten: boolean) => void;
  /** The shape being drawn, painted over the plan until it becomes a feature. */
  readonly draft?: Draft;
  readonly draftReadout?: string;
  /** Whether the static background ruling is drawn behind the plan. */
  readonly showGrid?: boolean;
  /**
   * Plan units per metre, including the editor's estimate for a plan nobody has measured.
   *
   * Passed in rather than read off the floorplan: the document's own figure is null on every
   * stored plan, and a desk sized from null is the fixed dot this used to draw at every zoom
   * on every plan regardless of how big the building is.
   */
  readonly unitsPerMetre?: number | null;
  /** Feature ids of desks that have no backing workplace record, and so cannot be booked. */
  readonly unbookableDeskIds?: readonly string[];
  /** Feature ids of desks whose retirement date has arrived, so they are out of service now. */
  readonly retiredDeskIds?: ReadonlySet<string>;
  /** Where the pointer is, in plan coordinates, for the status bar's readout. */
  readonly onPointerAt?: (at: Coordinate | undefined) => void;
  /** True while the vertex tool is armed: the selection's points are shown and grabbable. */
  readonly vertexMode?: boolean;
  /** How the selection's outline answers a press. Wins over [vertexMode] when given. */
  readonly reshape?: ReshapeMode;
  /** Which features a click may reach, for an editing mode. Everything, when absent. */
  readonly accept?: (feature: EditorFeature) => boolean;
  /** Select without dragging, for shapes that are only ever reshaped. */
  readonly lockMove?: boolean;
  /** Layers drawn faded, so the one an editing mode works on stands out. */
  readonly dimmedLayers?: ReadonlySet<DrawLayerTag>;
  /**
   * The cursor while nothing more specific applies, for an editing mode to say it is armed.
   *
   * Only a fallback: the arrows over a grip and the crosshair of a drawing tool still win,
   * because those say what the next press does and this only says which mode is on.
   */
  readonly idleCursor?: string;
  /** Which snapping is switched on. Nothing snaps without it. */
  readonly snapSettings?: SnapSettings;
  /** How far apart the snapping grid's points are, in plan units. */
  readonly snapGridSpacing?: number;
  /**
   * Whether moved, reshaped, drawn and placed things line up with the rest of the plan, with
   * guide lines while they do. On unless switched off.
   */
  readonly autoAlign?: boolean;
  /** Opens the editor's context menu, with whatever was right-clicked under it. */
  readonly onContextMenu?: (
    position: { x: number; y: number },
    feature: EditorFeature | undefined,
  ) => void;
}

/**
 * The editor surface.
 *
 * Drawn on a `<canvas>` in immediate mode rather than as an element per feature: the plans
 * this has to carry run to hundreds of shapes each, and every pointer move during a drag
 * would otherwise reconcile that whole tree. Nothing here uses a mapping library — these
 * coordinates are each project's own arbitrary drawing units, with no projection, no tiles
 * and no basemap, so a map engine's entire contribution would be unused while its geographic
 * assumptions would actively get the maths wrong.
 *
 * What to draw is decided by `buildDrawPlan`, which is pure and tested; this component owns
 * only the element, its size, and when to repaint.
 */
/** How close, in screen pixels, something has to come to lining up before it is lined up. */
const ALIGN_REACH_PIXELS = 6;

/** How close, in screen pixels, a shape point has to be to pull the pointer onto it. */
const SNAP_REACH_PIXELS = 10;

const FloorplanCanvas = ({
  floorPlan,
  viewport,
  width,
  height,
  layerVisibility,
  selection,
  document: editorDocument,
  onPlace,
  onDraw,
  onDrawMove,
  draft,
  draftReadout,
  showGrid,
  unitsPerMetre,
  unbookableDeskIds = [],
  retiredDeskIds,
  vertexMode = false,
  reshape,
  accept,
  lockMove,
  dimmedLayers,
  idleCursor,
  snapSettings,
  snapGridSpacing = 0,
  autoAlign = true,
  onPointerAt,
  onContextMenu,
}: Props): React.JSX.Element => {
  const reshapeMode: ReshapeMode = reshape ?? (vertexMode ? 'corners' : 'off');
  const iconsLoaded = useMdiIcons();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // What the element actually measures. Started at the surface size so the first paint has
  // something sensible, then followed by a ResizeObserver: a drawing tool that leaves a
  // margin of dead grey around a fixed-size canvas looks like a page, not a workspace.
  const [size, setSize] = useState({ width, height });

  // Which element the pointer is over. Selection tells you what you have; hover tells you
  // what you would get — without it, small desks on a busy plan are a guessing game.
  const [hoveredKey, setHoveredKey] = useState<string | undefined>();

  const projection = useMemo(
    () => createProjection(computeBoundingBox(floorPlan), width, height),
    [floorPlan, width, height],
  );

  // The stable callback, not the viewport object. `useViewport` returns a fresh object every
  // render, so depending on it tore down and rebuilt the observer on every render of the
  // editor — and `observe()` fires a callback immediately, which set state, which rendered
  // again. Not a runaway loop, because a canvas-only render leaves the prop identity alone;
  // but with the pointer readout setting state at the root of the editor it was a new
  // observer on every mouse move across the drawing.
  const { setAspect } = viewport;

  useEffect(() => {
    const wrapper = wrapperRef.current;

    // Absent in jsdom, and in any browser old enough not to have it. Falling back to the
    // surface size keeps the canvas working rather than collapsing it to nothing.
    if (!wrapper || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;

      if (!box || box.width <= 0 || box.height <= 0) {
        return;
      }

      const next = { width: Math.round(box.width), height: Math.round(box.height) };

      // Bailed out when nothing moved. A new object of equal numbers is still a state change
      // to React, which is half of what made the loop above run.
      setSize((current) => (
        current.width === next.width && current.height === next.height ? current : next
      ));

      // The viewport fits the plan to the window, so it needs the window's shape. Without it
      // the fit is computed against the surface's 3:2 and letterboxed into whatever the panel
      // actually is, which leaves the plan small in the middle of dead workspace.
      setAspect(next.width / next.height);
    });

    observer.observe(wrapper);

    return () => observer.disconnect();
  }, [setAspect]);

  const { viewBox } = viewport;

  // What the surface is not drawing right now. Hit-testing reads the document rather than
  // the screen, so without this a retired desk stays clickable out of an empty patch of floor.
  const hiddenIds = useMemo(() => {
    const hidden = new Set<string>();

    if (!layerVisibility.desks || !layerVisibility.retiredDesks) {
      floorPlan.desks
        .filter((desk) => !layerVisibility.desks || retiredDeskIds?.has(desk.featureId))
        .forEach((desk) => hidden.add(desk.id));
    }

    if (!layerVisibility.deskShapes) {
      floorPlan.deskShapes.forEach((shape) => hidden.add(shape.id));
    }

    if (!layerVisibility.labels) {
      floorPlan.labels.forEach((label) => hidden.add(label.id));
    }

    // The drawn areas too, now that they are clickable: a hidden room must not be selectable
    // out of what looks like empty floor.
    if (!layerVisibility.rooms) {
      floorPlan.rooms.forEach((room) => hidden.add(room.id));
    }

    if (!layerVisibility.floor) {
      floorPlan.floorBackdrop.forEach((area) => hidden.add(area.id));
    }

    if (!layerVisibility.border) {
      floorPlan.border.forEach((area) => hidden.add(area.id));
    }

    return hidden;
  }, [floorPlan, layerVisibility, retiredDeskIds]);

  // Where the last snap pulled to, drawn as a marker so a snapped point is never a surprise.
  const [snapMarker, setSnapMarker] = useState<SnapResult | undefined>();
  const snapping = !!snapSettings && (snapSettings.toShapes || snapSettings.toGrid);

  /**
   * The points a shape snap may pull to, built only when a snap asks for them.
   *
   * Two lists: everything, for drawing and placing; and everything but the selection, for while
   * the selection is being dragged. The second is frozen for the length of a gesture — dragging
   * changes only the selection, so the rest of the plan cannot move under it, and rebuilding the
   * list from a new document on every frame walked every corner of the plan twice per mouse move.
   * It is thrown away when the next press starts, so each gesture begins from the plan as it is.
   */
  const allTargets = useRef<{ plan: EditableFloorPlan; hidden: ReadonlySet<string>; targets: Coordinate[] }>();
  // Keyed by what is left out: a press selects what it lands on, and the first frame of the
  // drag can run before that selection has rendered — a list frozen then would still hold the
  // dragged shape's own corners, and it would stick where it started.
  const targetsBesideSelection = useRef<{ excluded: string; targets: Coordinate[] } | undefined>();

  const targetsFor = useCallback((exclude: readonly string[]): Coordinate[] => {
    if (exclude.length) {
      const excluded = [...exclude].sort().join('|');

      if (targetsBesideSelection.current?.excluded !== excluded) {
        targetsBesideSelection.current = {
          excluded,
          targets: snapTargetsOf(floorPlan, new Set(exclude), hiddenIds),
        };
      }

      return targetsBesideSelection.current.targets;
    }

    const cached = allTargets.current;

    if (!cached || cached.plan !== floorPlan || cached.hidden !== hiddenIds) {
      allTargets.current = { plan: floorPlan, hidden: hiddenIds, targets: snapTargetsOf(floorPlan, new Set(), hiddenIds) };
    }

    return allTargets.current?.targets ?? [];
  }, [floorPlan, hiddenIds]);

  const selectedIds = useMemo(() => selection.features.map((feature) => feature.id), [selection.features]);

  const snap = useMemo(() => {
    if (!snapping || !snapSettings) {
      return undefined;
    }

    // Ten pixels on screen, in plan units: the same reach to the hand at every zoom.
    const onScreen = surfaceTransformOf(size.width, size.height, viewport.viewBox).scale * projection.scale;
    const tolerance = onScreen > 0 ? SNAP_REACH_PIXELS / onScreen : 0;

    return (at: Coordinate, excludeSelection: boolean): Coordinate => {
      const result = snapPoint(at, snapSettings, {
        targets: snapSettings.toShapes ? targetsFor(excludeSelection ? selectedIds : []) : [],
        tolerance,
        gridSpacing: snapGridSpacing,
      });

      setSnapMarker((current) => {
        if (result.kind === 'none') return undefined;
        return current && current.kind === result.kind
          && current.at[0] === result.at[0] && current.at[1] === result.at[1] ? current : result;
      });

      return result.at;
    };
  }, [
    snapping, snapSettings, size, viewport.viewBox, projection.scale, targetsFor, selectedIds, snapGridSpacing,
  ]);

  // The guides of the alignment in progress, in plan units, and nothing between gestures.
  const [guides, setGuides] = useState<AlignmentGuide[]>([]);

  const align = useMemo(() => {
    if (!autoAlign) {
      return undefined;
    }

    // A few pixels on screen, in plan units: near enough that lining up feels like a pull
    // towards what is intended, far enough that nothing jumps unasked across a gap.
    const onScreen = surfaceTransformOf(size.width, size.height, viewport.viewBox).scale * projection.scale;
    const tolerance = onScreen > 0 ? ALIGN_REACH_PIXELS / onScreen : 0;

    return (
      moving: readonly Coordinate[],
      options: {
        readonly exclude: readonly string[];
        readonly extraTargets?: readonly Coordinate[];
        readonly lockX?: boolean;
        readonly lockY?: boolean;
      },
    ): { dx: number; dy: number } => {
      const base = targetsFor(options.exclude);
      const targets = options.extraTargets?.length ? [...base, ...options.extraTargets] : base;
      const result = alignPoints(moving, targets, tolerance, options);

      setGuides((current) => (
        current.length === 0 && result.guides.length === 0 ? current : result.guides
      ));

      return { dx: result.dx, dy: result.dy };
    };
  }, [autoAlign, size, viewport.viewBox, projection.scale, targetsFor]);

  // A marker left over from the last gesture would point at nothing once snapping goes off.
  useEffect(() => {
    if (!snapping) setSnapMarker(undefined);
  }, [snapping]);

  const interaction = useCanvasInteraction({
    floorPlan,
    projection,
    viewport,
    selection,
    document: editorDocument,
    onPlace,
    onDraw,
    onDrawMove,
    onHover: setHoveredKey,
    onPointerAt,
    hiddenIds,
    onContextMenu,
    reshape: reshapeMode,
    accept,
    lockMove,
    snap,
    align,
  });

  // Only while something is actually being lined up. A guide left on screen after the release
  // would be pointing at a relationship that no longer means anything.
  const showGuides = guides.length > 0 && (interaction.dragging || !!onDraw || !!onPlace);

  // Only while something is being placed, drawn or dragged: that is when a point is being
  // snapped, and a marker hanging about after the release would be pointing at nothing.
  const showSnapMarker = snapMarker && (interaction.dragging || !!onDraw || !!onPlace);

  const commands = useMemo(
    () => buildDrawPlan(floorPlan, {
      showFloor: layerVisibility.floor,
      showRooms: layerVisibility.rooms,
      showBorder: layerVisibility.border,
      showDesks: layerVisibility.desks,
      showDeskShapes: layerVisibility.deskShapes,
      showLabels: layerVisibility.labels,
      showRetiredDesks: layerVisibility.retiredDesks,
      selectedKeys: selection.keys,
      hoveredKey,
      retiredDeskIds,
      unbookableDeskIds,
      iconPathOf: getMdiIconPath,
      iconsLoaded,
      planScale: projection.scale,
      unitsPerMetre: floorPlan.unitsPerMetre ?? unitsPerMetre ?? null,
    }),
    [
      floorPlan,
      layerVisibility,
      selection.keys,
      hoveredKey,
      retiredDeskIds,
      unbookableDeskIds,
      iconsLoaded,
      projection.scale,
      unitsPerMetre,
    ],
  );

  // No box and no grips while reshaping. The corners and sides are what is being worked on,
  // and a resize grip sitting on top of a corner makes the two impossible to tell apart.
  const selectionBounds = useMemo(
    () => (reshapeMode === 'off' ? boundsOf(selection.features, projection) : undefined),
    [reshapeMode, selection.features, projection],
  );

  // Only while reshaping, and only for the selection: every corner of every shape on a plan
  // is thousands of dots, which is not a drawing anybody can work on.
  const vertices = useMemo(() => {
    if (reshapeMode !== 'corners') {
      return [];
    }

    return selection.features
      .filter(isAreaFeature)
      .flatMap((feature) => feature.coordinates.flatMap(
        (polygon) => polygon.flatMap((ring) => ring.map((vertex) => toScreen(vertex, projection))),
      ));
  }, [reshapeMode, selection.features, projection]);

  // The middle of every side: a grip to add a corner at, or to take hold of the side by.
  const edgeHandles = useMemo(() => {
    if (reshapeMode === 'off') {
      return [];
    }

    return selection.features
      .filter(isAreaFeature)
      .flatMap((feature) => feature.coordinates.flatMap((polygon) => polygon.flatMap(
        (ring) => ring.slice(1).map((vertex, index) => {
          const from = toScreen(ring[index], projection);
          const to = toScreen(vertex, projection);

          return { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
        }),
      )));
  }, [reshapeMode, selection.features, projection]);

  // Painted in a layout effect rather than an effect, so the canvas is never shown for a
  // frame holding the previous view — which reads as a flicker on every pan.
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !context) {
      return undefined;
    }

    const ratio = window.devicePixelRatio || 1;

    // Sized in backing-store pixels and scaled back down in CSS, so the drawing is sharp on a
    // high-density screen instead of being upscaled from a third of the pixels.
    if (canvas.width !== Math.round(size.width * ratio) || canvas.height !== Math.round(size.height * ratio)) {
      canvas.width = Math.round(size.width * ratio);
      canvas.height = Math.round(size.height * ratio);
    }

    // Coalesced to one paint per frame: a pointer move can fire several times between frames,
    // and painting a 400-feature plan for each of them is work nobody ever sees.
    const frame = requestAnimationFrame(() => paint(context, {
      commands,
      projection,
      viewBox,
      width: size.width,
      height: size.height,
      devicePixelRatio: ratio,
      marquee: interaction.marquee,
      selectionBounds,
      selectionResizable: isResizable(selection.features),
      readout: interaction.readout,
      draft,
      draftReadout,
      showGrid,
      vertices,
      edgeHandles,
      dimmedLayers,
      snapGridSpacing: snapSettings?.toGrid ? snapGridSpacing : undefined,
      snapMarker: showSnapMarker ? { ...toScreen(snapMarker.at, projection), kind: snapMarker.kind } : undefined,
      guides: showGuides
        ? guides.map((guide) => ({
          from: toScreen(guide.axis === 'x' ? [guide.value, guide.from] : [guide.from, guide.value], projection),
          to: toScreen(guide.axis === 'x' ? [guide.value, guide.to] : [guide.to, guide.value], projection),
        }))
        : undefined,
    }));

    return () => cancelAnimationFrame(frame);
  }, [
    commands,
    projection,
    viewBox,
    size,
    interaction.marquee,
    interaction.readout,
    selectionBounds,
    selection.features,
    draft,
    draftReadout,
    showGrid,
    vertices,
    edgeHandles,
    dimmedLayers,
    snapSettings?.toGrid,
    snapGridSpacing,
    showSnapMarker,
    snapMarker,
    showGuides,
    guides,
  ]);

  const cursor = (): string => {
    // A crosshair for anything that puts a point down, drawing included: the pointer has to
    // say that a click will commit something rather than select.
    if (onPlace || onDraw) return 'crosshair';
    if (interaction.rotating) return 'grabbing';
    if (interaction.resizing) return 'nwse-resize';
    if (interaction.dragging) return 'move';
    // A grab hand on empty surface is the affordance for dragging the plan around; it is
    // what tells an admin the gesture exists at all.
    if (interaction.panning) return 'grabbing';
    // Over a shape the mode can edit, the mode's own cursor stays: it already says "click to
    // edit this", and swapping it for a plain hand would hide which mode is armed.
    if (interaction.hoverCursor && !(idleCursor && interaction.hoverCursor === 'pointer')) {
      return interaction.hoverCursor;
    }

    return idleCursor ?? 'grab';
  };

  return (
    <Box ref={wrapperRef} sx={{ position: 'absolute', inset: 0 }}>
      <canvas
        ref={canvasRef}
        // Named, but given no role: a canvas carrying pointer handlers is an interactive
        // surface, and labelling it as an image would tell a screen reader it is something to
        // read rather than something to use. What is on it is reported by the status bar and
        // the inspector beside it, which are real elements.
        aria-label={`Floorplan, storey ${floorPlan.storey}`}
        style={{
          width: '100%',
          height: '100%',
          cursor: cursor(),
          touchAction: 'none',
          display: 'block',
        }}
        onWheel={(event: WheelEvent<HTMLCanvasElement>): void => viewport.zoomAt(event)}
        onPointerDown={(event): void => {
          // A new gesture snaps against the plan as it is now, not as the last drag left it.
          targetsBesideSelection.current = undefined;
          interaction.onSurfacePointerDown(event);
        }}
        onPointerMove={interaction.onSurfacePointerMove}
        onPointerUp={(event): void => {
          setGuides([]);
          interaction.onSurfacePointerUp(event);
        }}
        onPointerCancel={(event): void => {
          setGuides([]);
          interaction.onSurfacePointerCancel(event);
        }}
        onContextMenu={interaction.onSurfaceContextMenu}
        onPointerLeave={(): void => {
          setHoveredKey(undefined);
          setSnapMarker(undefined);
          setGuides([]);

          // Cleared on the way out, so the readout does not keep showing where the pointer
          // was ten minutes ago as though it were still there.
          onPointerAt?.(undefined);
        }}
      />
    </Box>
  );
};

export default FloorplanCanvas;

export type { PointerEvent };
